import {type ApolloError} from '@apollo/client';
import {useMemo} from 'react';

import {useHistoryKeyInfoQueryVars} from '../../state/runs/hooks';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import {useNgwTruncatedPanelGenLimits} from '../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import * as QueryTS from '../../util/queryts';
import {useMetricSearch} from '../../util/useMetricSearch';

const DEFAULT_PAGE_SIZE = 100;

export type MetricKeySearchResult = {
  key: string;
  types: string[];
};

const EMPTY_RESULTS: MetricKeySearchResult[] = [];

export type UseMetricKeySearchOptions = {
  /**
   * Any-match membership on `MetricInfo.types` (e.g. `['number']` for the
   * x-axis, `['number', 'histogram']` for the y-axis). Applied by the backend
   * via the `metricTypes` arg.
   */
  types?: string[];
  metricNamePattern?: string;
  pageSize?: number;
  skip?: boolean;
};

export function useMetricKeySearch(
  runSetRefs: RunSetTypes.Ref[],
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined,
  {
    types,
    metricNamePattern,
    pageSize = DEFAULT_PAGE_SIZE,
    skip = false,
  }: UseMetricKeySearchOptions = {}
): {
  results: MetricKeySearchResult[];
  hasMore: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  retry: () => void;
} {
  const vars = useHistoryKeyInfoQueryVars(runSetRefs, customRunNamesRef);
  const {
    config: {runLimit},
  } = useNgwTruncatedPanelGenLimits();

  const {data, loading, error, retry} = useMetricSearch({
    entityName: vars.entityName,
    projectName: vars.projectName,
    filters: vars.filters,
    metricNamePattern,
    pageSize,
    requiredRunKeys: vars.requiredRunKeys,
    runLimit,
    runOrder: QueryTS.sortToOrderString(vars.sort),
    types,
    skip,
  });

  const results = useMemo<MetricKeySearchResult[]>(() => {
    const metrics = data?.metrics;
    if (metrics == null || metrics.length === 0) {
      return EMPTY_RESULTS;
    }
    return metrics;
  }, [data]);

  return {
    results,
    hasMore: data?.hasMore ?? false,
    loading,
    error,
    retry,
  };
}
